import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

const BN = {
  backHome:           'Laman Utama',
  title:              '🔔 Pemberitahuan Ibu Bapa',
  subtitle:           'Pemberitahuan Kehadiran Ibu Bapa',
  unacknowledged:     'belum diakui',
  recordAttendance:   '+ Rekod Kehadiran',
  refreshButton:      '↻',
  errorLabel:         'Ralat / Error',
  retryButton:        'Cuba Lagi / Retry',
  emptyTitle:         'Tiada Pemberitahuan',
  emptySubtitle:      'Tiada amaran ketidakhadiran belum diakui. Semua pelajar hadir!',
  consecutive:        '× berturut-turut',
  dateLabel:          'Tarikh:',
  parentContact:      '📱 Maklumat Ibu Bapa / Parent Contact',
  parentName:         'Nama:',
  parentPhone:        'Telefon:',
  parentEmail:        'Emel:',
  cancelButton:       'Batal',
  acknowledgeButton:  'Akui / Acknowledge',
  noteLabel:          'Nota penyelesaian (pilihan) / Resolution note (optional)',
  notePlaceholder:    'Contoh: Ibu bapa telah dihubungi melalui WhatsApp.',
  acknowledging:      'Mengakui…',
  confirmButton:      '✓ Akui Pemberitahuan / Confirm Acknowledgement',
  alertTypeAbsence:          'Ketidakhadiran / Absence',
  alertTypeConsecutive:      'Ketidakhadiran Berulang / Consecutive',
};

const EN = {
  backHome:           'Home',
  title:              '🔔 Parent Notifications',
  subtitle:           'Parent Attendance Notifications',
  unacknowledged:     'unacknowledged',
  recordAttendance:   '+ Attendance',
  refreshButton:      '↻',
  errorLabel:         'Error',
  retryButton:        'Retry',
  emptyTitle:         'No Notifications',
  emptySubtitle:      'No unacknowledged absence alerts. All students are present!',
  consecutive:        '× consecutive',
  dateLabel:          'Date:',
  parentContact:      '📱 Parent Contact',
  parentName:         'Name:',
  parentPhone:        'Phone:',
  parentEmail:        'Email:',
  cancelButton:       'Cancel',
  acknowledgeButton:  'Acknowledge',
  noteLabel:          'Resolution note (optional)',
  notePlaceholder:    'E.g. Parent contacted via WhatsApp.',
  acknowledging:      'Acknowledging…',
  confirmButton:      '✓ Confirm Acknowledgement',
  alertTypeAbsence:          'Absence',
  alertTypeConsecutive:      'Consecutive Absence',
};

interface Props { user: AuthUser }

interface Alert {
  id:                   string;
  alert_type:           string;
  consecutive_absences: number;
  triggered_date:       string;
  acknowledged_at:      string | null;
  student_name:         string;
  student_ic:           string | null;
  parent_name:          string | null;
  parent_email:         string | null;
  parent_phone:         string | null;
  school_name:          string | null;
  school_code:          string | null;
  created_at:           string;
}

function getSchoolId(user: AuthUser): string | null {
  if (user.roles.some((r) => r.role === 'super_admin' && r.schoolId === null)) return null;
  return user.roles.find((r) => r.schoolId)?.schoolId ?? null;
}

export function AlertsPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;

  const ALERT_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
    ABSENCE:             { label: UI.alertTypeAbsence,    color: '#fb923c' },
    CONSECUTIVE_ABSENCE: { label: UI.alertTypeConsecutive, color: '#f87171' },
  };

  const schoolId = getSchoolId(user);

  const [alerts,     setAlerts]     = useState<Alert[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [ackId,      setAckId]      = useState<string | null>(null);
  const [ackNote,    setAckNote]    = useState('');
  const [ackLoading, setAckLoading] = useState(false);
  const [toast,      setToast]      = useState<{ message: string; variant: ToastVariant } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (schoolId) params.set('schoolId', schoolId);
      const data = await apiGet<Alert[]>(`/api/students/sessions/alerts?${params}`);
      setAlerts(data);
    } catch (err) {
      const msg = err instanceof TypeError && err.message.includes('fetch')
        ? 'Tidak dapat menghubungi pelayan svc-sis. / Cannot reach svc-sis.'
        : err instanceof Error ? err.message : 'Ralat tidak diketahui.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => { void load(); }, [load]);

  async function handleAcknowledge(id: string) {
    if (ackLoading) return;
    setAckLoading(true);
    try {
      await apiPost(`/api/students/sessions/alerts/${id}/acknowledge`, { note: ackNote.trim() || undefined });
      setToast({ variant: 'success', message: 'Amaran telah diakui. / Alert acknowledged.' });
      setAckId(null);
      setAckNote('');
      void load();
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Gagal mengakui amaran. / Failed to acknowledge.',
      });
    } finally {
      setAckLoading(false);
    }
  }

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

          {/* Nav */}
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← {UI.backHome}</Link>
          </div>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                {UI.title}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {UI.subtitle} — {alerts.length} {UI.unacknowledged}
              </p>
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
              <Link to="/admin/attendance">
                <Button variant="secondary" size="sm">{UI.recordAttendance}</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
                {loading ? <Spinner size="sm" /> : UI.refreshButton}
              </Button>
            </div>
          </div>

          {/* Content */}
          {error ? (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg }}>
              <p style={{ margin: 0, color: '#f87171', fontWeight: typography.fontWeight.semibold }}>{UI.errorLabel}</p>
              <p style={{ margin: `${spacing.xs} 0 ${spacing.sm}`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{error}</p>
              <Button variant="secondary" size="sm" onClick={() => void load()}>{UI.retryButton}</Button>
            </div>
          ) : loading && alerts.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>
          ) : alerts.length === 0 ? (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center', boxShadow: shadows.xl }}>
              <div style={{ fontSize: '48px', marginBottom: spacing.md }}>✅</div>
              <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.lg }}>
                {UI.emptyTitle}
              </p>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {UI.emptySubtitle}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              {alerts.map((alert) => {
                const cfg = ALERT_TYPE_CONFIG[alert.alert_type] ?? { label: alert.alert_type, color: '#fb923c' };
                const isExpanded = ackId === alert.id;

                return (
                  <div
                    key={alert.id}
                    style={{
                      backgroundColor: colors.neutral.charcoal,
                      borderRadius:    borderRadius.xl,
                      border:          `1px solid ${cfg.color}44`,
                      boxShadow:       shadows.lg,
                      overflow:        'hidden',
                    }}
                  >
                    {/* Alert header */}
                    <div style={{ padding: spacing.lg, display: 'flex', gap: spacing.md, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
                          <span style={{
                            padding: `2px ${spacing.xs}`,
                            backgroundColor: `${cfg.color}22`, color: cfg.color,
                            borderRadius: borderRadius.full, fontSize: typography.fontSize.xs,
                            fontWeight: typography.fontWeight.bold,
                          }}>
                            {cfg.label}
                          </span>
                          {alert.consecutive_absences > 1 && (
                            <span style={{ fontSize: typography.fontSize.xs, color: '#f87171' }}>
                              {alert.consecutive_absences}{UI.consecutive}
                            </span>
                          )}
                        </div>

                        <p style={{ margin: `0 0 ${spacing.xs}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.base }}>
                          {alert.student_name}
                        </p>

                        {alert.student_ic && (
                          <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            IC: {alert.student_ic}
                          </p>
                        )}

                        <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                          {UI.dateLabel} <strong style={{ color: colors.neutral.gray200 }}>
                            {new Date(alert.triggered_date).toLocaleDateString('ms-BN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          </strong>
                        </p>

                        {/* Parent contact — the "notification" target */}
                        {(alert.parent_name || alert.parent_email || alert.parent_phone) && (
                          <div style={{
                            marginTop: spacing.sm,
                            padding: spacing.sm,
                            backgroundColor: 'rgba(247,208,0,0.06)',
                            borderRadius: borderRadius.md,
                            border: '1px solid rgba(247,208,0,0.15)',
                          }}>
                            <p style={{ margin: `0 0 2px`, fontSize: typography.fontSize.xs, color: colors.primary.yellow, fontWeight: typography.fontWeight.semibold }}>
                              {UI.parentContact}
                            </p>
                            {alert.parent_name  && <p style={contactLine()}><strong>{UI.parentName}</strong> {alert.parent_name}</p>}
                            {alert.parent_phone && <p style={contactLine()}><strong>{UI.parentPhone}</strong> {alert.parent_phone}</p>}
                            {alert.parent_email && <p style={contactLine()}><strong>{UI.parentEmail}</strong> {alert.parent_email}</p>}
                          </div>
                        )}
                      </div>

                      <Button
                        variant={isExpanded ? 'ghost' : 'primary'}
                        size="sm"
                        onClick={() => {
                          setAckId(isExpanded ? null : alert.id);
                          setAckNote('');
                        }}
                      >
                        {isExpanded ? UI.cancelButton : UI.acknowledgeButton}
                      </Button>
                    </div>

                    {/* Acknowledge panel */}
                    {isExpanded && (
                      <div style={{ padding: spacing.lg, borderTop: `1px solid ${colors.neutral.gray800}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <label style={{ display: 'block', fontSize: typography.fontSize.sm, color: colors.neutral.gray400, marginBottom: spacing.xs }}>
                          {UI.noteLabel}
                        </label>
                        <textarea
                          value={ackNote}
                          onChange={(e) => setAckNote(e.target.value)}
                          placeholder={UI.notePlaceholder}
                          rows={2}
                          style={{
                            width: '100%', backgroundColor: colors.neutral.black,
                            border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
                            color: colors.neutral.white, padding: spacing.sm,
                            fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.body,
                            resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                            marginBottom: spacing.sm,
                          }}
                        />
                        <Button
                          variant="primary" size="sm"
                          onClick={() => void handleAcknowledge(alert.id)}
                          disabled={ackLoading}
                          loading={ackLoading}
                        >
                          {ackLoading ? UI.acknowledging : UI.confirmButton}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function contactLine(): React.CSSProperties {
  return { margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray200 };
}
